import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { companyApi } from '../lib/api-service';
import Logo from './Logo';
import NotificationDropdown from './NotificationDropdown';
import { IconButton } from './shared/StandardButtons';
import OrganizationThemeEditor from './theme/OrganizationThemeEditor';
import toast from 'react-hot-toast';

export default function Header({
  isSidebarCollapsed,
  sidebarWidth = 64, // Default width in Tailwind units
  sidebarMode = 'push' // 'push' or 'overlay'
}) {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [showEditModal, setShowEditModal] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newCompanyLogo, setNewCompanyLogo] = useState('');
  const [mandatoryTheme, setMandatoryTheme] = useState(null);
  const [showThemeEditor, setShowThemeEditor] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showNavDropdown, setShowNavDropdown] = useState(false);
  const navDropdownRef = useRef(null);

  // Check if user is admin
  const isAdmin = user?.roles?.includes('admin');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navDropdownRef.current && !navDropdownRef.current.contains(event.target)) {
        setShowNavDropdown(false);
      }
    };

    if (showNavDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showNavDropdown]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleNavItemClick = (path) => {
    navigate(path);
    setShowNavDropdown(false);
  };

  const handleOpenEdit = async () => {
    setNewCompanyName(user?.companyName || '');

    // Fetch current company data to get logo and mandatory theme
    try {
      const company = await companyApi.getCompany();
      setNewCompanyLogo(company.logo || '');
      setMandatoryTheme(company.mandatory_theme || null);
    } catch (error) {
      console.error('Failed to fetch company data:', error);
    }

    setShowEditModal(true);
  };

  const handleCloseEdit = () => {
    setShowEditModal(false);
    setNewCompanyName('');
    setNewCompanyLogo('');
    setMandatoryTheme(null);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }

      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image size must be less than 2MB');
        return;
      }

      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewCompanyLogo(reader.result);
      };
      reader.onerror = () => {
        toast.error('Failed to read image file');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setNewCompanyLogo('');
  };

  const handleUpdateCompany = async () => {
    if (!newCompanyName.trim()) {
      toast.error('Company name cannot be empty');
      return;
    }

    setIsUpdating(true);
    try {
      const updated = await companyApi.updateCompany(
        newCompanyName.trim(),
        newCompanyLogo || null,
        mandatoryTheme || null
      );

      // Update user info in storage with new company name and logo
      const storage = localStorage.getItem('rememberMe') === 'true' ? localStorage : sessionStorage;
      const storedUser = storage.getItem('user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        userData.companyName = updated.name;
        userData.companyLogo = updated.logo;
        storage.setItem('user', JSON.stringify(userData));
        setUser(userData);
      }

      toast.success('Company updated successfully!');
      handleCloseEdit();
    } catch (error) {
      // Error already shown by API interceptor
      console.error('Failed to update company:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Convert width to pixels for inline styles
  const widthInPx = sidebarWidth * 4;

  return (
    <header
      className={`fixed top-0 right-0 h-16 bg-gray-900 border-b border-gray-700 z-30 transition-all duration-300`}
      style={{
        left: sidebarMode === 'overlay' || isSidebarCollapsed ? '0' : `${widthInPx}px`
      }}
    >
      <div className="h-full px-6 flex items-center justify-between">
        {/* Left: Company Name */}
        <div className="flex items-center space-x-3">
          {user?.companyLogo && (
            <img
              src={user.companyLogo}
              alt="Company logo"
              className="w-10 h-10 rounded-lg object-cover shadow-lg"
            />
          )}
          <h1 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent" style={{ color: 'var(--color-text-on-accent)' }}>
                      {user?.companyName || 'My Organization'}
          </h1>
          {isAdmin && (
            <IconButton onClick={handleOpenEdit} title="Edit organization">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </IconButton>
          )}
        </div>

        {/* Right: Notifications & Menu */}
        <div className="flex items-center space-x-4">
          <NotificationDropdown />

          {/* 3-dot menu dropdown */}
          <div className="relative" ref={navDropdownRef}>
            <IconButton onClick={() => setShowNavDropdown(!showNavDropdown)} title="Menu">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
            </IconButton>

            {showNavDropdown && (
              <div className="absolute right-0 mt-2 w-48 rounded-lg shadow-xl py-2 z-50" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border-default)', borderWidth: '1px' }}>
                <button
                  onClick={() => handleNavItemClick('/knowledge')}
                  className="w-full px-4 py-2 text-left transition-colors flex items-center space-x-3"
                  style={{ color: 'var(--color-text-secondary)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                    e.currentTarget.style.color = 'var(--color-text-primary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = 'var(--color-text-secondary)';
                  }}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <span>Library</span>
                </button>
                <button
                  onClick={() => handleNavItemClick('/questions')}
                  className="w-full px-4 py-2 text-left transition-colors flex items-center space-x-3"
                  style={{ color: 'var(--color-text-secondary)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                    e.currentTarget.style.color = 'var(--color-text-primary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = 'var(--color-text-secondary)';
                  }}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <span>Forum</span>
                </button>
                {isAdmin && (
                  <>
                    <div className="h-px my-2" style={{ backgroundColor: 'var(--color-border-default)' }}></div>
                    <button
                      onClick={() => handleNavItemClick('/admin')}
                      className="w-full px-4 py-2 text-left transition-colors flex items-center space-x-3"
                      style={{ color: 'var(--color-text-secondary)' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                        e.currentTarget.style.color = 'var(--color-text-primary)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = 'var(--color-text-secondary)';
                      }}
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>Admin</span>
                    </button>
                  </>
                )}
                <div className="h-px my-2" style={{ backgroundColor: 'var(--color-border-default)' }}></div>
                <button
                  onClick={handleLogout}
                  className="w-full px-4 py-2 text-left transition-colors flex items-center space-x-3"
                  style={{ color: 'var(--color-text-secondary)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                    e.currentTarget.style.color = 'var(--color-text-primary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = 'var(--color-text-secondary)';
                  }}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Company Name Modal */}
      {showEditModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={handleCloseEdit}
        >
          <div
            className="relative bg-gray-800 rounded-2xl shadow-2xl border border-gray-700 max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <div className="absolute top-4 right-4">
              <IconButton onClick={handleCloseEdit} title="Close">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </IconButton>
            </div>

            {/* Modal content */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent" style={{ color: 'var(--color-text-on-accent)' }}>
                              Edit Organization
              </h2>

              {/* Logo Upload Section */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Organization Logo
                </label>
                <div className="flex items-center space-x-4">
                  {/* Logo Preview */}
                  <div className="flex-shrink-0">
                    {newCompanyLogo ? (
                      <div className="relative w-20 h-20 rounded-lg overflow-hidden border-2 border-gray-700">
                        <img
                          src={newCompanyLogo}
                          alt="Logo preview"
                          className="w-full h-full object-cover"
                        />
                        <button
                          onClick={handleRemoveLogo}
                          className="absolute top-1 right-1 p-1 bg-red-600 rounded-full hover:bg-red-700 transition-colors"
                          title="Remove logo"
                        >
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <Logo
                        alt="Default logo"
                        className="h-20 max-w-[200px] object-contain"
                      />
                    )}
                  </div>

                  {/* Upload Button */}
                  <div className="flex-1">
                    <label className="block">
                      <span className="sr-only">Choose logo</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-600 file:text-white hover:file:bg-purple-700 file:cursor-pointer cursor-pointer"
                        style={{ color: 'transparent' }}
                      />
                    </label>
                    <p className="mt-1 text-xs text-gray-500">
                      PNG, JPG, GIF up to 2MB
                    </p>
                  </div>
                </div>
              </div>

              {/* Organization Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Organization Name
                </label>
                <input
                  type="text"
                  value={newCompanyName}
                  onChange={(e) => setNewCompanyName(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-900/50 border-2 border-gray-700 rounded-xl text-base text-gray-100 placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-4 focus:ring-purple-500/20 transition-all duration-200"
                  placeholder="Enter organization name"
                />
              </div>

              {/* Divider */}
              <div className="border-t border-gray-700 my-2" />

              {/* Mandatory Theme Section */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Mandatory Organization Theme
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Set a custom theme that all users in this organization must use. Users will not be able to change themes.
                </p>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setShowThemeEditor(true)}
                    className="px-4 py-2 rounded-lg text-sm font-semibold bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-4 focus:ring-purple-500/50 transition-all duration-200 flex items-center gap-2"
                    style={{ color: 'var(--color-text-on-accent)' }}
                    type="button"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                    </svg>
                    {mandatoryTheme ? 'Edit Theme' : 'Create Theme'}
                  </button>
                  {mandatoryTheme && (
                    <button
                      onClick={() => setMandatoryTheme(null)}
                      className="px-4 py-2 rounded-lg text-sm font-semibold text-red-400 bg-red-600/20 hover:bg-red-600/30 focus:outline-none focus:ring-4 focus:ring-red-500/50 transition-all duration-200"
                      type="button"
                    >
                      Remove Theme
                    </button>
                  )}
                  {mandatoryTheme && (
                    <span className="text-sm text-green-400 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Theme configured
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-3 pt-2">
                <button
                  onClick={handleCloseEdit}
                  disabled={isUpdating}
                  className="px-4 py-3 rounded-xl text-base font-semibold text-gray-300 bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-4 focus:ring-gray-500/50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Close
                </button>
                <button
                  onClick={handleUpdateCompany}
                  disabled={isUpdating}
                  className="flex-1 py-3 px-4 rounded-xl text-base font-semibold bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-4 focus:ring-purple-500/50 shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ color: 'var(--color-text-on-accent)' }}
                >
                  {isUpdating ? 'Updating...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Organization Theme Editor */}
      <OrganizationThemeEditor
        isOpen={showThemeEditor}
        onClose={() => setShowThemeEditor(false)}
        initialTheme={mandatoryTheme}
        onSave={(theme) => {
          setMandatoryTheme(theme);
          setShowThemeEditor(false);
        }}
      />
    </header>
  );
}
